import React, { forwardRef, useId } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CustomTextInput } from '../TextInput/TextInput';

interface IProps {
  label: string | null;
  type?: string;
  error?: boolean;
  helperText?: string | null;
  defaultValue?: string;
  multiple?: boolean;
  /* eslint-disable-next-line */
  value: any;
  selectOptions: { value: string; label: string; disabled?: boolean }[];
  disabled?: boolean;
  onChange?: (event: SelectChangeEvent, child: React.ReactNode) => void;
}

const SelectInput = forwardRef(
  (
    {
      label = null,
      type = 'text',
      error,
      helperText,
      selectOptions = [],
      defaultValue = '',
      multiple,
      value,
      ...other
    }: IProps,
    ref
  ) => {
    const inputId = useId();
    const labelId = `${inputId}-label`;

    return (
      <FormControl
        variant="standard"
        fullWidth
        error={error}
        sx={{ margin: '12px 0' }}
      >
        {label && (
          <InputLabel
            shrink
            id={labelId}
            htmlFor={inputId}
            sx={{
              color: '#5E6366',
              pl: 1,
              '&.Mui-focused': {
                color: error ? '#F9473C' : '#5E6366 !important',
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        {/* The component supports both scalar and multiple values. */}
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Select<any>
          labelId={labelId}
          id={inputId}
          input={<CustomTextInput />}
          defaultValue={defaultValue}
          displayEmpty
          sx={[
            helperText
              ? {
                  boxShadow: (theme) =>
                    `${alpha(theme.palette.error.main, 0.5)} 0 0 0 0.2rem`,
                  borderColor: (theme) => theme.palette.error.main,
                  borderRadius: '8px',
                }
              : {},
          ]}
          multiple={multiple}
          value={value}
          renderValue={(val) =>
            multiple && val ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {
                  /* eslint-disable-next-line */
                  val?.map((value: any) => (
                    <Chip
                      key={value}
                      label={
                        selectOptions?.find((o) => o?.value === value)?.label
                      }
                    />
                  ))
                }
              </Box>
            ) : (
              selectOptions?.find((o) => o.value === val)?.label
            )
          }
          {...other}
          ref={ref}
        >
          {selectOptions?.map((op, index) => {
            return (
              <MenuItem
                key={`${op.value}-${index}`}
                value={op.value}
                disabled={op.disabled}
                sx={index > 0 ? { borderTop: '1px solid #dfdfe4' } : {}}
              >
                {multiple && (
                  <Checkbox checked={value?.indexOf(op.value) > -1} />
                )}
                {op.label}
              </MenuItem>
            );
          })}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default SelectInput;
